import getSymbolFromCurrency from "currency-symbol-map";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import { isMobile } from "react-device-detect";
import toastr from "toastr";
import EmbedController from "../../../../embed/EmbedController";
import AlertSlackOfError from "../../../../monitoring/AlertSlackOfError";
import { addConfiguredProductToWishlist } from "../../../modules/addConfiguredProductToWishlist";
import { getDealerDiscountPercentage, getFinalPrice } from "../../../modules/getFinalPrice";
import { useProductScreenshotCapture } from "../../../modules/waitForProductScreenshot";
import { CreateScreens } from "../../../screens/CreateScreens";
import {
  components_state,
  is_experience_loaded_and_revealed,
  items_state,
  products_state,
  session_id,
  update_components_activeId,
  update_items_activeIds,
} from "../../dataManagers/GlobalDataManagers";
import { BikeModelSelectionModal } from "../BikeModelSelectionModal/BikeModelSelectionModal";
import { BuildSection } from "../BuildSection/BuildSection";
import { HeadersBuildSection } from "../BuildSection/HeadersBuildSection";
import ControlsIndicator from "../ControlsIndicator/ControlsIndicator";
import { HUD } from "../HUD/HUD";
import { RectangleButton } from "../RectangleButton/RectangleButton";
import { SceneLoadingScreen } from "../SceneLoadingScreen/SceneLoadingScreen";
import { SceneLoadingScreenTransparent } from "../SceneLoadingScreenTransparent/SceneLoadingScreenTransparent";
import { ShoppingCart } from "../ShoppingCart/ShoppingCart";
import "./styles.scss";

export const BuilderUI = () => {
  const [isExperienceLoadedAndRevealed] = useAtom(is_experience_loaded_and_revealed);
  // products
  const [productsState] = useAtom(products_state);

  // components
  const [componentsState] = useAtom(components_state);
  const [, setComponentsActiveId] = useAtom(update_components_activeId);
  const [, setComponentsState] = useAtom(components_state);

  // items
  const [itemsState] = useAtom(items_state);
  const [, setItemsActiveIds] = useAtom(update_items_activeIds);
  const sessionId = useAtomValue(session_id);
  const waitForProductScreenshot = useProductScreenshotCapture();

  const [showCartTerms, setShowCartTerms] = useState(false);
  const [isWishlistPending, setIsWishlistPending] = useState(false);
  const isWishlistPendingRef = useRef(false);

  // Display price: Shopify amounts as-is (already catalog-discounted for dealers)
  const finalPrice = getFinalPrice(productsState, componentsState, itemsState);

  // Dealer: log base discount % once the experience is ready
  useEffect(() => {
    if (!isExperienceLoadedAndRevealed) return;
    const discountPercentage = getDealerDiscountPercentage(productsState?.activeObj?.shopify);
    if (discountPercentage == null) return;
    console.log(`[dealer] discount percentage: ${discountPercentage}%`);
  }, [isExperienceLoadedAndRevealed, productsState?.activeObj?.shopify]);

  const handleShare = async () => {
    if (EmbedController.isEmbedded) {
      EmbedController.sendUrlShare();
      EmbedController.setShareUrlCallback((msg) => {
        toastr.info(msg);
      });
    } else {
      if (!navigator) return;
      try {
        const shareData = {
          title: "Thrill Seekers 3D Customizer",
          url: window.location.href,
        };

        await navigator.share(shareData);
        return;
      } catch (error) {
        await navigator.clipboard.writeText(window.location.href);
        toastr.info("Link copied");
      }
    }
  };

  // show the bike model selection modal after 5 seconds
  const [showBikeModal, setShowBikeModal] = useState(false);
  useEffect(() => {
    const applicableUrl = EmbedController?.parentUrl || window.location.href;
    const hasBikeModelRedirect = applicableUrl.includes("bikeModelRedirect=true");
    const isModelAlreadyPicked = !applicableUrl.includes("products/3d-customizer");

    // Only show the modal if there's no bike model redirect parameter
    if (!hasBikeModelRedirect && !isModelAlreadyPicked) {
      const timer = setTimeout(() => {
        setShowBikeModal(true);
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }
  }, []);

  // when a bike model is selected in the modal, this function is calledss
  const handleBikeSelect = (bikeModelSlug) => {
    const currentUrl = EmbedController?.parentUrl || window.location.href;
    if (bikeModelSlug === productsState.activeObj.shopify.handle) return;
    if (!currentUrl) return;
    const redirectUrl = currentUrl.replace(productsState.activeObj.shopify.handle, bikeModelSlug);
    const redirectUrlWithParam = new URL(redirectUrl);
    redirectUrlWithParam.searchParams.set("bikeModelRedirect", "true");
    if (EmbedController.isEmbedded) {
      EmbedController.sendParentUrlChange(redirectUrlWithParam.toString());
    } else {
      window.location.href = redirectUrlWithParam.toString();
    }
    setShowBikeModal(false);
  };

  const handleAddToWishlist = async () => {
    if (isWishlistPendingRef.current) return;
    isWishlistPendingRef.current = true;
    setIsWishlistPending(true);
    try {
      await addConfiguredProductToWishlist({
        productsState,
        componentsState,
        itemsState,
        waitForProductScreenshot,
        setItemsActiveIds,
        sessionId,
      });
    } catch (error) {
      AlertSlackOfError("BuilderUI.jsx handleAddToWishlist", `error: ${error}`, sessionId);
    } finally {
      isWishlistPendingRef.current = false;
      setIsWishlistPending(false);
    }
  };

  const [isRequiringModelSelectionForCheckout, setIsRequiringModelSelectionForCheckout] = useState(false);

  const isDemoModelSelected = useMemo(() => {
    return productsState?.activeObj?.shopify?.handle.includes("3d-customizer");
  }, [productsState?.activeObj?.shopify?.handle]);

  useEffect(() => {
    if (showCartTerms && isDemoModelSelected) {
      setIsRequiringModelSelectionForCheckout(true);
      setShowBikeModal(true);
      setShowCartTerms(false);
    }
    if (!isDemoModelSelected) {
      setIsRequiringModelSelectionForCheckout(false);
    }
  }, [showCartTerms, isDemoModelSelected]);

  const subheaderLabel = productsState.isPrimed && `${productsState.activeObj.shopify.displayName}`.trim();

  return (
    <>
      <Container fluid className="BuilderUI p-0">
        {/* UI section on left (desktop) or bottom (mobile) of screen */}
        {productsState.isPrimed && componentsState.isPrimed && itemsState.isPrimed && (
          <BuildSection
            steps={productsState.array[0].category}
            setShowCartTerms={() => setShowCartTerms(true)}
            componentsStateObj={{
              state: componentsState,
              setActiveId: setComponentsActiveId,
            }}
          >
            <HeadersBuildSection
              brandLogo="/images/brand-logo.png"
              // subheaderLabel={subheaderLabel.length > 40 ? subheaderLabel.substring(0, 40) + "..." : subheaderLabel}
              subheaderLabel={subheaderLabel}
              headerLineImgSrc="/images/bradleyHeaderLine.svg"
              finalPrice={parseFloat(finalPrice)}
              currencySymbol={getSymbolFromCurrency(productsState.activeObj?.shopify?.currency?.active ?? "USD")}
              setShowBikeModal={setShowBikeModal}
              productsState={productsState}
            />

            <div className="d-flex justify-content-center">
              <div style={{ width: "fit-content" }}>
                <hr className="my-2" />
                <h5 className="fw-bold mt-0 mb-2 text-black">CUSTOM SEAT COVER</h5>
              </div>
            </div>

            <CreateScreens productsState={productsState} componentsState={componentsState} itemsStateArray={itemsState.array} />

            <div className="build-section-add-to-cart-wrapper">
              <RectangleButton
                type="button"
                label="Add To Cart"
                iconSrc="/images/iconShoppingCart-black.svg"
                onClickHandler={() => setShowCartTerms(true)}
                gaObj={{
                  event: "Add-to-Cart-Bradley",
                  addToCartBtnLocation: "Build Section",
                }}
              />
            </div>

            {showCartTerms && !isRequiringModelSelectionForCheckout && (
              <ShoppingCart
                cartTermsObj={{
                  state: showCartTerms,
                  setState: setShowCartTerms,
                }}
                productsState={productsState}
                componentsState={componentsState}
                itemsState={itemsState}
              />
            )}

            <BikeModelSelectionModal
              show={showBikeModal}
              onHide={() => setShowBikeModal(false)}
              onSelectBike={handleBikeSelect}
              isRequiringModelSelectionForCheckout={isRequiringModelSelectionForCheckout}
            />
          </BuildSection>
        )}

        {/* UI section overlaying the 3D scene */}
        {/* Uses .shared-scene-sizing to mimic the scene's canvas. */}
        <HUD>
          {/* loading screen used for the initial load - it covers the scene's canvas */}
          <SceneLoadingScreen />
          {/* transparent loader for when assets are loading dynamically but we don't need to cover up whole screen */}
          <SceneLoadingScreenTransparent canBeVisible={isExperienceLoadedAndRevealed} />

          {productsState.isPrimed && componentsState.isPrimed && itemsState.isPrimed && (
            <>
              <ControlsIndicator />
              <div className={`containerWidgets h-100`}>
                <div className={`innerWrapper pointer-events-all ${isMobile ? "flexMobile" : "flexNormal"}`}>
                  <RectangleButton
                    label="Add To Cart"
                    iconSrc="/images/iconShoppingCart-black.svg"
                    onClickHandler={() => setShowCartTerms(true)}
                    gaObj={{
                      event: "Add-to-Cart-Bradley",
                      addToCartBtnLocation: "Scene",
                    }}
                  />
                  <div className="hud-secondary-actions">
                    <RectangleButton
                      iconOnly
                      ariaLabel="Share"
                      iconSrc="/images/share.svg"
                      onClickHandler={() => handleShare()}
                      gaObj={{
                        event: "Share-Thrill",
                        ShareButtonLocation: "Scene",
                      }}
                    />
                    <RectangleButton
                      type="button"
                      iconOnly
                      ariaLabel="Add to wishlist"
                      iconSrc="/images/iconHeart.svg"
                      onClickHandler={() => handleAddToWishlist()}
                      disabled={isWishlistPending}
                      gaObj={{ event: "Add-to-Wishlist", addToWishlistBtnLocation: "Scene" }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </HUD>
      </Container>
    </>
  );
};
